import type { IMultiCheckboxSubFormConfig } from "./MultiCheckboxSubForm.types";
import { dateFormat, i18nCrossUtil } from "@adgm/libs";

const crossIntl = i18nCrossUtil.t();

/**
 * WealthSourceOfOwnerForm 配置对象
 * 用于驱动通用 MultiCheckboxSubForm 组件
 */
export const WEALTH_SOURCE_OF_OWNER_CONFIG: IMultiCheckboxSubFormConfig = {
  options: [
    {
      label: () => crossIntl["kyc.v2.option.label.salary.bonus.from.employment"],
      description: () => crossIntl["kyc.v2.option.description.salary.bonus.from.employment"],
      fieldKey: "salary",
      subFormFields: [
        {
          name: "name",
          label: () => crossIntl["kyc.v2.form.label.employer.name"],
          type: "input",
          required: true,
        },
        {
          name: "address",
          label: () => crossIntl["kyc.v2.form.label.employer.address"],
          type: "input",
          required: true,
        },
        {
          name: "businessSector",
          label: () => crossIntl["kyc.v2.form.label.business.sector"],
          type: "input",
          required: true,
        },
        {
          name: "salaryPerAnnum",
          label: () => crossIntl["kyc.v2.form.label.salary.per.annum.only"],
          type: "input",
          required: true,
        },
        {
          name: "yearsOfService",
          label: () => crossIntl["kyc.v2.form.label.years.working.at.employer"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        name: "",
        address: "",
        businessSector: "",
        salaryPerAnnum: "",
        yearsOfService: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.self.employment.business.ownership"],
      description: () => crossIntl["kyc.v2.option.description.self.employment.business.ownership"],
      fieldKey: "selfEmployment",
      subFormFields: [
        {
          name: "nameAndAddress",
          label: () => crossIntl["kyc.v2.form.label.name.and.address.of.business"],
          type: "input",
          required: true,
        },
        {
          name: "annualProfit",
          label: () => crossIntl["kyc.v2.form.label.annual.profit"],
          type: "input",
          required: true,
        },
        {
          name: "businessNature",
          label: () => crossIntl["kyc.v2.form.label.nature.of.business"],
          type: "input",
          required: true,
        },
        {
          name: "percentage",
          label: () => crossIntl["kyc.v2.form.label.ownership.percentage.in.business"],
          type: "input",
          required: true,
        },
        {
          name: "businessYearsInOperation",
          label: () => crossIntl["kyc.v2.form.label.business.years.in.operation"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        nameAndAddress: "",
        annualProfit: "",
        businessNature: "",
        percentage: "",
        businessYearsInOperation: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.sale.of.assets"],
      description: () => crossIntl["kyc.v2.option.description.sale.of.assets"],
      fieldKey: "assetSale",
      subFormFields: [
        {
          name: "assetType",
          label: () => crossIntl["kyc.v2.form.label.type.of.asset"],
          type: "input",
          required: true,
        },
        {
          name: "saleDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.sale"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "saleAmount",
          label: () => crossIntl["kyc.v2.form.label.total.sale.amount"],
          type: "input",
          required: true,
        },
        {
          name: "holdPeriod",
          label: () => crossIntl["kyc.v2.form.label.how.long.asset.owned"],
          type: "input",
          required: true,
        },
        {
          name: "originatingSource",
          label: () => crossIntl["kyc.v2.form.label.originating.source.of.asset"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        assetType: "",
        saleDate: "",
        saleAmount: "",
        holdPeriod: "",
        originatingSource: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.inheritance"],
      description: () => crossIntl["kyc.v2.option.description.inheritance.wrong"],
      fieldKey: "inheritance",
      subFormFields: [
        {
          name: "deceasedName",
          label: () => crossIntl["kyc.v2.form.label.name.of.deceased"],
          type: "input",
          required: true,
        },
        {
          name: "dateOfDeath",
          label: () => crossIntl["kyc.v2.form.label.date.of.death"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "relationship",
          label: () => crossIntl["kyc.v2.form.label.relationship.to.customer"],
          type: "input",
          required: true,
        },
        {
          name: "receivedInheritance",
          label: () => crossIntl["kyc.v2.form.label.inheritance.received"],
          type: "input",
          required: true,
        },
        {
          name: "inheritanceDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
      ],
      createInitialValue: () => ({
        deceasedName: "",
        dateOfDeath: "",
        relationship: "",
        receivedInheritance: "",
        inheritanceDate: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.profits.returns.dividends"],
      description: () => crossIntl["kyc.v2.option.description.profits.returns.dividends"],
      fieldKey: "investmentPortfolioManagement",
      subFormFields: [
        {
          name: "dividendReceiptDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt.of.dividend.profit"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "amountReceived",
          label: () => crossIntl["kyc.v2.display.label.total.amount.received"],
          type: "input",
          required: true,
        },
        {
          name: "payingCompanyName",
          label: () =>
            crossIntl["kyc.v2.form.label.name.of.company.paying.dividend.type.of.assets.traded"],
          type: "input",
          required: true,
        },
        {
          name: "holdPeriod",
          label: () =>
            crossIntl[
              "kyc.v2.form.label.length.of.time.shares.held.time.period.managing.portfolio"
            ],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        dividendReceiptDate: "",
        amountReceived: "",
        payingCompanyName: "",
        holdPeriod: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.gifts"],
      description: () => crossIntl["kyc.v2.option.description.gifts"],
      fieldKey: "gift",
      subFormFields: [
        {
          name: "receiptDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "amountReceived",
          label: () => crossIntl["kyc.v2.display.label.total.amount.received"],
          type: "input",
          required: true,
        },
        {
          name: "relationship",
          label: () => crossIntl["kyc.v2.form.label.relationship.to.ubo"],
          type: "input",
          required: true,
        },
        {
          name: "giftReason",
          label: () => crossIntl["kyc.v2.form.label.reason.for.gift"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        receiptDate: "",
        amountReceived: "",
        relationship: "",
        giftReason: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.legal.settlements.awards"],
      description: () => crossIntl["kyc.v2.option.description.legal.settlements.awards"],
      fieldKey: "legalSettlements",
      subFormFields: [
        {
          name: "detail",
          label: () => crossIntl["kyc.v2.form.label.details.of.legal.settlement"],
          type: "input",
          required: true,
        },
        {
          name: "paymentIssuingCountry",
          label: () => crossIntl["kyc.v2.form.label.payment.issuing.party"],
          type: "input",
          required: true,
        },
        {
          name: "settlementAmount",
          label: () => crossIntl["kyc.v2.form.label.settlement.amount"],
          type: "input",
          required: true,
        },
        {
          name: "settlementDate",
          label: () => crossIntl["kyc.v2.form.label.settlement.date"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
      ],
      createInitialValue: () => ({
        detail: "",
        paymentIssuingCountry: "",
        settlementAmount: "",
        settlementDate: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.winnings.windfall"],
      description: () => crossIntl["kyc.v2.option.description.winnings.windfall"],
      fieldKey: "winnings",
      subFormFields: [
        {
          name: "date",
          label: () => crossIntl["kyc.v2.form.label.date.of.winning.windfall"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "nature",
          label: () => crossIntl["kyc.v2.form.label.nature.of.windfall"],
          type: "input",
          required: true,
        },
        {
          name: "amount",
          label: () => crossIntl["kyc.v2.form.label.windfall.amount"],
          type: "input",
          required: true,
        },
        {
          name: "nameOfEntityIssuingPayment",
          label: () => crossIntl["kyc.v2.form.label.name.of.entity.issuing.payment"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        date: "",
        nature: "",
        amount: "",
        nameOfEntityIssuingPayment: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.intellectual.property"],
      description: () => crossIntl["kyc.v2.option.description.intellectual.property"],
      fieldKey: "intellectualProperty",
      subFormFields: [
        {
          name: "type",
          label: () => crossIntl["kyc.v2.form.label.type.of.intellectual.property"],
          type: "input",
          required: true,
        },
        {
          name: "fundReceived",
          label: () => crossIntl["kyc.v2.form.label.fund.received"],
          type: "input",
          required: true,
        },
        {
          name: "dateReceived",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt.of.funds"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
      ],
      createInitialValue: () => ({
        type: "",
        dateReceived: "",
        fundReceived: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.rental.income"],
      description: () => crossIntl["kyc.v2.option.description.rental.income"],
      fieldKey: "rentalIncome",
      subFormFields: [
        {
          name: "propertyAddress",
          label: () => crossIntl["kyc.v2.form.label.rental.property.address"],
          type: "input",
          required: true,
        },
        {
          name: "periodRented",
          label: () => crossIntl["kyc.v2.form.label.property.owned.and.rented.period"],
          type: "input",
          required: true,
        },
        {
          name: "rentalAmount",
          label: () => crossIntl["kyc.v2.form.label.rental.amount"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        propertyAddress: "",
        periodRented: "",
        rentalAmount: "",
      }),
    },
    {
      label: () => crossIntl["kyc.other.input"],
      description: () => crossIntl["kyc.v2.option.description.other"],
      fieldKey: "other",
      isOther: true,
      otherPlaceholder: () => crossIntl["kyc.other.input"],
      otherTitleFieldName: "title",
      subFormFields: [
        {
          name: "wealthSourceNature",
          label: () => crossIntl["kyc.v2.form.label.nature.of.source.of.wealth"],
          type: "input",
          required: true,
        },
        {
          name: "dateReceived",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "amountReceived",
          label: () => crossIntl["kyc.v2.display.label.total.amount.received"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        title: "",
        wealthSourceNature: "",
        dateReceived: "",
        amountReceived: "",
      }),
    },
  ],
};
